import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridEditModal } from "../editSearch/gridEditModal.tsx";
import { EditCartModal } from "./editCartModal.tsx";
import { DataGrid } from "../dataGrid/dataGrid.tsx";
import { ParamType } from "../../types/grid.ts";
import { useToast } from "../../hooks/useToast.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { formatInstant, parseServerTimestamp, toDateInput } from "../../util/serverTime.ts";
import { getPagePath } from "../../pages/pageRegistry.ts";
import { PagePopup } from "../dataGrid/pagePopup.tsx";
import {
    queueEmailTemplate,
    saveNote,
    stopCharging,
    ignoreTransaction,
    assignPromotions,
    sendKioskCommandVideo,
    kioskVideoUrl,
    returnReserveTransaction,
    RESERVE_ROW_TYPE,
    type TransactionDiscRow,
} from "../../api/specialized.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { batchedServicePost } from "../../api/readBatch.ts";
import { getEmailTemplateInfoCached, type EmailTemplateInfo } from "../../api/emailTemplate.ts";
import { ResendEmailModal } from "../customerEmail/resendEmailModal.tsx";
import { handleEmailLogButton, type ResendArgs } from "../customerEmail/handleEmailLogButton.ts";
import { RentalReturnsCard } from "./rentalReturnsCard.tsx";
import styles from "./transactionOpsModal.module.scss";

export interface TransactionOpsArgs {
    transactionID: number;
    instantID: string;
    rowID: number;
    discID: number;
    rfid: string;
    productID: number;
    productName: string;
    customerID: number;
    kioskID: number;
    isLocked: boolean;
    hasRental: boolean;
}

// ── Types ──────────────────────────────────────────────────

interface PaymentLogEntry {
    iPaymentLogId: number;
    sPaymentID: string;
    sType: string;
    sMethod: string;
    iAmount: number | null;
    iTax: number | null; // null on payment log rows without tax
    dWhen: string;
    sResultData: string;
    sCustomData?: string;
}

interface PaymentGroup {
    label: string;
    rows: PaymentLogEntry[];
}

// ── Helpers ────────────────────────────────────────────────

function groupPaymentLogs(logs: PaymentLogEntry[]): PaymentGroup[] {
    const groups: Record<string, PaymentGroup> = {
        reservation: { label: "Reservation", rows: [] },
        initial: { label: "Initial", rows: [] },
        late: { label: "Late", rows: [] },
        refund: { label: "Refund", rows: [] },
        unknown: { label: "Unknown", rows: [] },
    };
    for (const entry of logs) {
        const method = entry.sMethod?.toUpperCase() ?? "";
        const type = entry.sType?.toUpperCase() ?? "";
        let group: PaymentGroup;
        if (method === "RETURN" || method === "REFUND") {
            group = groups.refund!;
        } else {
            switch (type) {
                case "RESERVATION": case "RE_AUTH": case "COP_INCR": case "COP_AUTH":
                    group = groups.reservation!; break;
                case "PAYMENT": case "COP_PAYMENT": case "COP_CAPTURE":
                    group = groups.initial!; break;
                case "LATE PAYMENT":
                    group = groups.late!; break;
                default:
                    group = groups.unknown!; break;
            }
        }
        group.rows.push(entry);
    }
    return Object.values(groups).filter(g => g.rows.length > 0);
}

/** dWhen is SQL-server wall clock (the GETDATE() convention) and arrives without
 *  a zone, so it has to be resolved against the server zone first. */
function fmtDate(d: string): string {
    if (!d) return "";
    return formatInstant(parseServerTimestamp(d)) || d;
}

function fmtAmt(n: number | null | undefined): string { return (n ?? 0).toFixed(2); }

/** Pick interesting fields from raw transaction data for a summary view.
 *  Note: `InstantID` (from Info) and `InstantTransactionID` (from Head) carry
 *  the same instant ID — only one is listed here, the other is dropped both
 *  from the summary and the merged "All fields" list below it. */
const SUMMARY_FIELDS = [
    "TransactionID", "InstantID", "KioskID", "CustomerID",
    "PaymentDate", "LatePaymentDate", "Finished",
    "StopChargingInitialFee", "StopChargingLateFee",
    "TotalAmount", "TotalTax", "Currency",
];

function pickSummary(
    info: Record<string, unknown> | null,
    head: Record<string, unknown> | null,
): [string, string][] {
    // Merge so fields living on either object surface in the summary
    // (e.g. TransactionID is on Info, KioskID is on Head). Info wins on
    // overlap — it carries the customer-service-shaped projection.
    const data: Record<string, unknown> = { ...(head ?? {}), ...(info ?? {}) };
    const result: [string, string][] = [];
    for (const key of SUMMARY_FIELDS) {
        if (key in data && data[key] != null && data[key] !== "") {
            result.push([key, String(data[key])]);
        }
        if (key === "InstantID") {
            const code = returnCode(data.InstantTransactionID ?? data.InstantID);
            if (code) result.push(["ReturnCode", code]);
        }
    }
    return result;
}

/** The code a customer types at the kiosk to return a rental: the last 6
 *  characters of the instant ID, or the whole ID when it is shorter. Same rule
 *  as the PadelReturn_Code email (EmailTemplateLib PadelReturnCode.vb) and
 *  InventoryOS PadelSpec.GetReturnCode. */
function returnCode(instantID: unknown): string {
    const id = instantID == null ? "" : String(instantID).trim();
    return id.length > 6 ? id.slice(-6) : id;
}

// ── Tab system ─────────────────────────────────────────────

type TabId = "overview" | "payments" | "actions" | "comms";

function TabBar({ active, onChange, tabs }: {
    active: TabId;
    onChange: (id: TabId) => void;
    tabs: { id: TabId; label: string; hidden?: boolean }[];
}) {
    return (
        <div className={styles.tabBar}>
            {tabs.filter(t => !t.hidden).map(t => (
                <button
                    key={t.id}
                    className={`${styles.tab}${active === t.id ? ` ${styles.tabActive}` : ""}`}
                    onClick={() => onChange(t.id)}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// ── Overview Tab ───────────────────────────────────────────

function OverviewTab({ info, head }: {
    info: Record<string, unknown> | null;
    head: Record<string, unknown> | null;
}) {
    const summary = useMemo(() => pickSummary(info, head), [info, head]);
    // Merge Info + Head into one flat list, dropping entries the user can
    // already see elsewhere in the modal:
    //   - keys already shown in the summary grid above,
    //   - explicit excludes (StopChargingInitial* surfaces via Stop Charging,
    //     PaymentLogID is metadata not useful in the overview),
    //   - same-key duplicates between Info and Head,
    //   - and value-level duplicates for ID-shaped strings (e.g. InstantID
    //     and InstantTransactionID carry the same instant ID).
    const allFields = useMemo(() => {
        const summaryKeys = new Set(summary.map(([k]) => k));
        const summaryValuesLong = new Set(
            summary
                .map(([, v]) => v)
                .filter(v => typeof v === "string" && v.length > 3),
        );
        const exclude = new Set([
            "InstantTransactionID", // alias of InstantID — already in summary
            "StopChargingInitialFee",
            "StopChargingInitialFeeReason",
            "PaymentLogID",
        ]);
        const seenKeys = new Set<string>();
        const out: [string, string][] = [];
        for (const source of [info, head]) {
            if (!source) continue;
            for (const [k, v] of Object.entries(source)) {
                if (seenKeys.has(k) || summaryKeys.has(k) || exclude.has(k)) continue;
                if (typeof v === "string" && v.length > 3 && summaryValuesLong.has(v)) continue;
                seenKeys.add(k);
                out.push([k, v == null ? "" : String(v)]);
            }
        }
        return out;
    }, [info, head, summary]);

    return (
        <div className={styles.tabContent}>
            {/* Summary */}
            <div className={styles.summaryGrid}>
                {summary.map(([k, v]) => (
                    <div key={k} className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{k}</span>
                        <span className={styles.summaryValue}>{v}</span>
                    </div>
                ))}
            </div>

            {/* All remaining fields — merged from Info + Head, deduped + excluded. */}
            {allFields.length > 0 && (
                <div className={styles.detailsBlock}>
                    <div className={styles.detailsHeader}>All fields ({allFields.length})</div>
                    <div className={styles.tableScroll}>
                    <table className={styles.kvTable}>
                        <tbody>
                            {allFields.map(([k, v]) => (
                                <tr key={k}>
                                    <td className={styles.kvLabel}>{k}</td>
                                    <td className={styles.kvValue}>{v}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Payments Tab ───────────────────────────────────────────

/** Read-only list of the payment logs. Refunds are made through Edit
 *  Transaction (EditCartModal), not from here. */
function PaymentsTab({ groups }: { groups: PaymentGroup[] }) {
    if (groups.length === 0) return <p className={styles.muted}>No payment logs found</p>;

    return (
        <div className={styles.tabContent}>
            {groups.map(group => (
                <div key={group.label} className={styles.paymentGroup}>
                    <h4 className={styles.groupTitle}>{group.label} <span className={styles.badge}>{group.rows.length}</span></h4>
                    <div className={styles.tableScroll}>
                    <table className={styles.paymentTable}>
                        <thead>
                            <tr>
                                <th>Reference</th>
                                <th className={styles.thRight}>Price</th>
                                <th className={styles.thRight}>Tax</th>
                                <th className={styles.thRight}>Total</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.rows.map(entry => (
                                <tr key={entry.iPaymentLogId}>
                                    <td>{entry.sPaymentID}</td>
                                    <td className={styles.tdRight}>{fmtAmt(entry.iAmount)}</td>
                                    <td className={styles.tdRight}>{fmtAmt(entry.iTax)}</td>
                                    <td className={styles.tdRight}>{fmtAmt((entry.iAmount ?? 0) + (entry.iTax ?? 0))}</td>
                                    <td>{fmtDate(entry.dWhen)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Actions Tab ────────────────────────────────────────────

function ActionsTab({
    args, info, onUpdated,
    canStopCharging, canAssignPromo, canReturnOps,
    discRows, kioskOffset, hasRental,
}: {
    args: TransactionOpsArgs;
    info: Record<string, unknown> | null;
    onUpdated: () => void;
    canStopCharging: boolean;
    canAssignPromo: boolean;
    canReturnOps: boolean;
    discRows: TransactionDiscRow[] | null;
    kioskOffset: number;
    hasRental: boolean;
}) {
    const { toast } = useToast();
    const { cache } = useAuth();
    const { confirm } = useAlertDialog();

    // Reserve return — transaction-level (whole reservation), gated by the DB flag
    // and only shown when the transaction actually has a reserve ROW (type 45) — the
    // reserve signal is the row type, not a payment group.
    const flags = useMemo(() => getDbFeatureFlags(cache), [cache]);
    const [reserveBusy, setReserveBusy] = useState(false);
    const hasReserve = (discRows ?? []).some(r => r.TransactionTypeID === RESERVE_ROW_TYPE);
    // Reuses the existing "Product · show Reserve block" flag — a DB that prices reserve
    // products is the same one whose transactions can be set reserve-returned.
    const showReserveReturn = !!flags.ProductShowReserve && hasReserve && canReturnOps;

    const handleReserveReturn = async () => {
        const ok = await confirm({
            title: "Set this reservation as returned?",
            body: (
                <p style={{ margin: 0 }}>
                    Sets the reservation status to <b>RETURN</b> (via PayaP) and stamps the transaction head with an
                    all-returned date. This applies to the <b>whole reservation</b>, not individual items.
                </p>
            ),
            confirmLabel: "Set as returned",
            destructive: true,
        });
        if (!ok) return;
        setReserveBusy(true);
        try {
            const instantId = String(info?.InstantTransactionID ?? args.instantID);
            const res = await returnReserveTransaction(instantId);
            if (res.bError || res.oData !== true) {
                toast(res.sMessage ?? "Failed to set reservation as returned", "error");
            } else {
                toast("Reservation set as returned", "success");
                onUpdated();
            }
        } finally {
            setReserveBusy(false);
        }
    };

    // ── Stop charging state
    const [initialReason, setInitialReason] = useState("");
    const [lateReason, setLateReason] = useState("");
    const [chargingSaving, setChargingSaving] = useState(false);

    const stopInitial = !!info?.StopChargingInitialFee;
    const stopLate = !!info?.StopChargingLateFee;
    const initialPaid = !!info?.PaymentDate;
    const latePaid = !!info?.LatePaymentDate;
    const finished = !!info?.Finished;

    const handleChargeToggle = async (isInitial: boolean) => {
        const currentStop = isInitial ? stopInitial : stopLate;
        const reason = isInitial ? initialReason : lateReason;
        setChargingSaving(true);
        try {
            const result = await stopCharging(args.transactionID, isInitial, !currentStop, !currentStop ? reason : null);
            if (result.bError) { toast(result.sMessage ?? "Failed", "error"); }
            else { toast(!currentStop ? "Charging stopped" : "Charging resumed", "success"); onUpdated(); }
        } finally { setChargingSaving(false); }
    };

    // ── Promotion state
    const [promoId, setPromoId] = useState<number | "">("");
    const [codeCount, setCodeCount] = useState(1);
    const [validTo, setValidTo] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() + 30); return toDateInput(d);
    });
    const [promoSaving, setPromoSaving] = useState(false);
    const [promoResult, setPromoResult] = useState<string | null>(null);

    const promotions = (cache?.Promotions as Array<{ PromotionID: number; Title: string; AdminPromotion?: boolean }> | undefined)
        ?.filter(p => p.AdminPromotion) ?? [];

    const handleAssignPromo = async () => {
        if (!promoId) return;
        setPromoSaving(true); setPromoResult(null);
        try {
            const res = await assignPromotions(Number(promoId), codeCount, validTo, args.transactionID, args.customerID, args.kioskID);
            if (res.bError) { toast(res.sMessage ?? "Failed", "error"); }
            else if (res.oData) {
                const codes = res.oData.filter(c => c.Code !== "No Code Available");
                setPromoResult(codes.length ? `Codes: ${codes.map(c => c.Code).join(", ")}` : "No codes available");
                toast(codes.length === codeCount ? "Codes assigned" : (codes.length ? "Partially assigned" : "No codes available"), codes.length ? "success" : "error");
            }
        } finally { setPromoSaving(false); }
    };

    // ── Video state
    const [videoRequesting, setVideoRequesting] = useState(false);
    const { activeEndpoint } = useAuth();

    const handleRequestVideo = async () => {
        const txId = String(info?.InstantTransactionID ?? args.instantID);
        setVideoRequesting(true);
        try {
            const result = await sendKioskCommandVideo(args.kioskID, "UPLOADVIDEOS", 0, txId);
            toast(!result.bError && result.oData ? "Video requested. Check back in a few minutes." : "Failed to request video",
                !result.bError && result.oData ? "success" : "error");
        } finally { setVideoRequesting(false); }
    };

    const handleViewVideo = () => {
        const txId = String(info?.InstantTransactionID ?? args.instantID);
        window.open(kioskVideoUrl(activeEndpoint?.Title, args.kioskID, txId), "_blank", "resizable=yes,height=600,width=800");
    };

    // ── Ignore state
    const [ignored, setIgnored] = useState(false);
    const handleIgnore = async () => {
        const next = !ignored;
        const result = await ignoreTransaction(args.transactionID, next);
        if (!result.bError && result.oData === true) { setIgnored(next); toast(next ? "Transaction ignored" : "Transaction unignored", "success"); }
        else { toast("Failed to update", "error"); }
    };

    return (
        <div className={styles.tabContent}>
            {/* Stop Charging */}
            {canStopCharging && info && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Stop Charging</h4>
                    <div className={styles.chargingGrid}>
                        <div className={styles.chargingRow}>
                            <span className={styles.chargingLabel}>Initial Fee</span>
                            {stopInitial ? (
                                <span className={styles.chargingStatus}>Stopped{info.StopChargingInitialFeeReason ? `: ${info.StopChargingInitialFeeReason}` : ""}</span>
                            ) : (
                                <input className={styles.textInput} placeholder="Reason..." value={initialReason} onChange={e => setInitialReason(e.target.value)} disabled={initialPaid || finished} style={{ flex: 1 }} />
                            )}
                            <button className={stopInitial ? styles.actionBtn : styles.refundBtn} onClick={() => handleChargeToggle(true)} disabled={chargingSaving || initialPaid || finished}>
                                {stopInitial ? "Resume" : "Stop"}
                            </button>
                        </div>
                        <div className={styles.chargingRow}>
                            <span className={styles.chargingLabel}>Late Fee</span>
                            {stopLate ? (
                                <span className={styles.chargingStatus}>Stopped{info.StopChargingLateFeeReason ? `: ${info.StopChargingLateFeeReason}` : ""}</span>
                            ) : (
                                <input className={styles.textInput} placeholder="Reason..." value={lateReason} onChange={e => setLateReason(e.target.value)} disabled={latePaid || finished} style={{ flex: 1 }} />
                            )}
                            <button className={stopLate ? styles.actionBtn : styles.refundBtn} onClick={() => handleChargeToggle(false)} disabled={chargingSaving || latePaid || finished}>
                                {stopLate ? "Resume" : "Stop"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reservation return — transaction-level (whole reservation). Shown only
                when the DB flag is on AND the transaction has a reservation. */}
            {showReserveReturn && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Reservation</h4>
                    <div className={styles.actionRow}>
                        <button className={styles.refundBtn} onClick={handleReserveReturn} disabled={reserveBusy}>
                            {reserveBusy ? "Setting…" : "Set Reservation as Returned"}
                        </button>
                        <span className={styles.muted}>Whole reservation — not per-item.</span>
                    </div>
                </div>
            )}

            {/* Rental returns — rendered only when the bundle confirmed the
                transaction actually has rental rows the user can see. */}
            {hasRental && (
                <RentalReturnsCard rows={discRows ?? []} kioskOffset={kioskOffset}
                    transactionId={args.transactionID} canWrite={canReturnOps} onChanged={onUpdated} />
            )}

            {/* Promotions */}
            {canAssignPromo && promotions.length > 0 && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Assign Promotion</h4>
                    <div className={styles.actionRow}>
                        <select className={styles.selectInput} value={promoId} onChange={e => setPromoId(e.target.value ? Number(e.target.value) : "")}>
                            <option value="">Select promotion...</option>
                            {promotions.map(p => <option key={p.PromotionID} value={p.PromotionID}>{p.Title}</option>)}
                        </select>
                        <select className={styles.selectInput} value={codeCount} onChange={e => setCodeCount(Number(e.target.value))} style={{ minWidth: 90, width: 90 }}>
                            <option value={1}>1 code</option>
                            <option value={2}>2 codes</option>
                            <option value={3}>3 codes</option>
                        </select>
                        <input className={styles.textInput} type="date" value={validTo} onChange={e => setValidTo(e.target.value)} />
                        <button className={styles.actionBtn} onClick={handleAssignPromo} disabled={promoSaving || !promoId}>
                            {promoSaving ? "Assigning..." : "Assign"}
                        </button>
                    </div>
                    {promoResult && <p className={styles.promoResult}>{promoResult}</p>}
                </div>
            )}

            {/* Video */}
            {args.kioskID > 0 && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Video</h4>
                    <div className={styles.actionRow}>
                        <button className={styles.cancelBtn} onClick={handleRequestVideo} disabled={videoRequesting}>
                            {videoRequesting ? "Requesting..." : "Request Video"}
                        </button>
                        <button className={styles.cancelBtn} onClick={handleViewVideo}>View Video</button>
                    </div>
                </div>
            )}

            {/* Ignore */}
            <div className={styles.actionCard}>
                <div className={styles.actionRow}>
                    <button className={ignored ? styles.actionBtn : styles.cancelBtn} onClick={handleIgnore}>
                        {ignored ? "Unignore Transaction" : "Ignore Transaction"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Communications Tab ─────────────────────────────────────

function CommsTab({ args, canReadEmails, canReadNotes }: {
    args: TransactionOpsArgs;
    canReadEmails: boolean;
    canReadNotes: boolean;
}) {
    const { toast } = useToast();
    const [resendArgs, setResendArgs] = useState<ResendArgs | null>(null);
    // Templates load lazily on first Comms-tab open and are cached in
    // emailTemplate.ts for the rest of the session — re-opening the modal
    // hits memory, not the server.
    const [emailInfo, setEmailInfo] = useState<EmailTemplateInfo | null>(null);
    const [templateId, setTemplateId] = useState<number | "">("");
    const [address, setAddress] = useState("");
    const [sending, setSending] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [noteSaving, setNoteSaving] = useState(false);
    const [notesKey, setNotesKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        getEmailTemplateInfoCached().then(info => { if (!cancelled) setEmailInfo(info); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const handleSendReceipt = async () => {
        if (!templateId || !address.trim()) return;
        setSending(true);
        try {
            const result = await queueEmailTemplate({ EmailTemplateID: Number(templateId), Address: address.trim(), PrimaryID: args.transactionID, SecondaryID: args.customerID });
            if (result.bError) { toast(result.sMessage ?? "Failed to send", "error"); }
            else { toast("Receipt queued", "success"); setAddress(""); }
        } finally { setSending(false); }
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        setNoteSaving(true);
        try {
            const result = await saveNote({ TransactionID: args.transactionID, CustomerID: args.customerID, KioskID: args.kioskID, ProductID: args.discID, Note: noteText.trim() });
            if (result.bError) { toast(result.sMessage ?? "Failed", "error"); }
            else { toast("Note saved", "success"); setNoteText(""); setNotesKey(k => k + 1); }
        } finally { setNoteSaving(false); }
    };

    const templates = emailInfo?.Templates ?? [];

    return (
        <div className={styles.tabContent}>
            {/* Send Receipt */}
            <div className={styles.actionCard}>
                <h4 className={styles.cardTitle}>Send Receipt</h4>
                <div className={styles.actionRow}>
                    <select className={styles.selectInput} value={templateId} onChange={e => setTemplateId(e.target.value ? Number(e.target.value) : "")}>
                        <option value="">Select template...</option>
                        {templates.map(t => <option key={t.TemplateID} value={t.TemplateID}>{t.TemplateName}</option>)}
                    </select>
                    <input className={styles.textInput} type="email" placeholder="Email address" value={address} onChange={e => setAddress(e.target.value)} style={{ flex: 1 }} />
                    <button className={styles.actionBtn} onClick={handleSendReceipt} disabled={sending || !templateId || !address.trim()}>
                        {sending ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>

            {/* Sent Emails Grid */}
            {canReadEmails && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Sent Emails</h4>
                    <DataGrid
                        name="customerEmailLog"
                        staticFilters={[
                            { sColumn: "iTransactionID", oValue: args.transactionID, Type: ParamType.Equal },
                            { sColumn: "iUserID", oValue: args.customerID, Type: ParamType.Equal },
                        ]}
                        autoSearch compact
                        onButtonClick={(info, row, colIndex) => handleEmailLogButton(info, row, colIndex, toast, setResendArgs)}
                    />
                </div>
            )}

            {/* Notes */}
            {canReadNotes && (
                <div className={styles.actionCard}>
                    <h4 className={styles.cardTitle}>Notes</h4>
                    <div className={styles.actionRow}>
                        <input className={styles.textInput} type="text" placeholder="Add a note..." value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleAddNote(); }}
                            style={{ flex: 1 }}
                        />
                        <button className={styles.actionBtn} onClick={handleAddNote} disabled={noteSaving || !noteText.trim()}>
                            {noteSaving ? "Saving..." : "Add Note"}
                        </button>
                    </div>
                    <DataGrid
                        key={notesKey}
                        name="TRANSACTIONNOTES"
                        staticFilters={[{ sColumn: "@TransactionID", oValue: args.transactionID, Type: ParamType.Specified }]}
                        autoSearch compact
                    />
                </div>
            )}

            {resendArgs && (
                <ResendEmailModal
                    open
                    emailLogId={resendArgs.emailLogId}
                    transactionId={resendArgs.transactionId}
                    customerEmail={resendArgs.customerEmail}
                    onClose={() => setResendArgs(null)}
                />
            )}
        </div>
    );
}

// ── Main Modal ─────────────────────────────────────────────

interface TransactionOpsModalProps {
    open: boolean;
    args: TransactionOpsArgs;
    onClose: () => void;
}

export function TransactionOpsModal({ open, args, onClose }: TransactionOpsModalProps) {

    const { cache } = useAuth();

    const [info, setInfo] = useState<Record<string, unknown> | null>(null);
    const [head, setHead] = useState<Record<string, unknown> | null>(null);
    const [paymentGroups, setPaymentGroups] = useState<PaymentGroup[]>([]);
    // Rental-only slices; server populates these only when the transaction
    // actually has rental rows the caller is permitted to see.
    const [discRows, setDiscRows] = useState<TransactionDiscRow[] | null>(null);
    const [kioskOffset, setKioskOffset] = useState(0);
    const [hasRental, setHasRental] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabId>("overview");
    const mountedTabs = useRef(new Set<TabId>(["overview"]));
    const handleTabChange = useCallback((id: TabId) => {
        mountedTabs.current.add(id);
        setActiveTab(id);
    }, []);

    const hasPermission = useCallback((pageName: string, write = false) => {
        const page = cache?.PageInfo?.Page?.find(pg => pg.Name?.toLowerCase() === pageName.toLowerCase());
        if (!page) return false;
        const perm = cache?.Permissions?.find(p => p.RowID === page.RowID);
        return write ? (perm?.Write ?? false) : (perm?.Read ?? false);
    }, [cache]);

    const canReadNotes = hasPermission("TRANSACTIONNOTES");
    const canReadEmails = hasPermission("customerEmailLog");
    const canStopCharging = hasPermission("execTransactionStopCharging", true);
    const canAssignPromo = hasPermission("execPromotionAssign", true);
    const canReturnOps = hasPermission("execTransactionReturnOps", true);

    // Boot the modal via the read coalescer — TransactionInformation +
    // TransactionHead + WebGetTransactionPaymentIDs always; TransactionDiscInfo +
    // WebGetKioskCurrentOffsetMinutes only when the caller has the rental-ops
    // permission (no point round-tripping a PermissionDenied otherwise).
    // HasRental is derived client-side from a non-empty DiscInfo slot.
    //
    // Every call is fired in the same tick so they coalesce into ONE
    // /api/v2/services-stream request, but each slot's state is applied the
    // instant *that* slot streams back — instead of awaiting the whole batch.
    // The "Loading…" shell clears as soon as the Overview-critical pair
    // (Information + Head) lands, so the window shows real content at the first
    // responses rather than the slowest one. StrictMode double-mount and the
    // dev double-fire are absorbed by batchedServicePost's own in-flight dedupe.
    const loadData = useCallback(() => {
        setLoading(true);
        let cancelled = false;

        const info = batchedServicePost<Record<string, unknown>>("TransactionInformation", { transactionID: args.transactionID });
        const head = batchedServicePost<Record<string, unknown>>("TransactionHead", { transactionID: args.transactionID });
        // VB param is `iTransactionID` (Hungarian) — name match is case-insensitive but not prefix-aware.
        const payments = batchedServicePost<PaymentLogEntry[]>("WebGetTransactionPaymentIDs", { iTransactionID: args.transactionID });

        void info.then(r => { if (!cancelled && !r.bError && r.oData) setInfo(r.oData); });
        void head.then(r => { if (!cancelled && !r.bError && r.oData) setHead(r.oData); });
        void payments.then(r => {
            if (cancelled || r.bError || !Array.isArray(r.oData)) return;
            setPaymentGroups(groupPaymentLogs(r.oData));
        });

        // Overview is the default tab — clear the loading shell once its two
        // inputs are in, not the whole fan-out. Error envelopes resolve (never
        // reject) so this can't hang.
        void Promise.all([info, head]).then(() => { if (!cancelled) setLoading(false); });

        if (canReturnOps) {
            const disc = batchedServicePost<TransactionDiscRow[]>("TransactionDiscInfo", { transactionID: args.transactionID });
            const offset = batchedServicePost<number>("WebGetKioskCurrentOffsetMinutes", { kioskID: args.kioskID });
            void Promise.all([disc, offset]).then(([discRes, offsetRes]) => {
                if (cancelled) return;
                if (!discRes.bError && Array.isArray(discRes.oData) && discRes.oData.length > 0) {
                    setDiscRows(discRes.oData);
                    setHasRental(true);
                    setKioskOffset(!offsetRes.bError ? (offsetRes.oData ?? 0) : 0);
                } else {
                    setDiscRows(null);
                    setHasRental(false);
                    setKioskOffset(0);
                }
            });
        } else {
            setDiscRows(null);
            setHasRental(false);
            setKioskOffset(0);
        }

        return () => { cancelled = true; };
    }, [args.transactionID, args.kioskID, canReturnOps]);

    const [showEditCart, setShowEditCart] = useState(false);
    const [eventPopupPath, setEventPopupPath] = useState<string | null>(null);

    useEffect(() => { if (open) return loadData(); }, [open, loadData]);

    const handleViewEvent = () => {
        const path = getPagePath("editcustomerevents");
        const sep = path.includes("?") ? "&" : "?";
        setEventPopupPath(`${path}${sep}sid=${encodeURIComponent(args.instantID)}&tid=${args.transactionID}`);
    };

    const footerLeft = (
        <>
            <button className={styles.linkBtn} onClick={handleViewEvent}>View Event</button>
            <button className={styles.linkBtn} onClick={() => setShowEditCart(true)}>Edit Transaction</button>
        </>
    );

    if (!open) return null;

    return (<>
        <GridEditModal
            title={`Transaction Operations — ${args.transactionID}`}
            wide
            readOnly
            cancelLabel="Close"
            loading={loading}
            footerLeft={footerLeft}
            onClose={onClose}
            onSave={() => { /* read-only */ }}
        >
            <div className={styles.container}>
                <TabBar
                    active={activeTab}
                    onChange={handleTabChange}
                    tabs={[
                        { id: "overview", label: "Overview" },
                        { id: "payments", label: `Payments${paymentGroups.length ? ` (${paymentGroups.reduce((s, g) => s + g.rows.length, 0)})` : ""}` },
                        { id: "actions", label: "Actions" },
                        { id: "comms", label: "Communications" },
                    ]}
                />

                <div className={activeTab === "overview" ? undefined : styles.tabHidden}>
                    {mountedTabs.current.has("overview") && <OverviewTab info={info} head={head} />}
                </div>
                <div className={activeTab === "payments" ? undefined : styles.tabHidden}>
                    {mountedTabs.current.has("payments") && (
                        <PaymentsTab groups={paymentGroups} />
                    )}
                </div>
                <div className={activeTab === "actions" ? undefined : styles.tabHidden}>
                    {mountedTabs.current.has("actions") && (
                        <ActionsTab args={args} info={info} onUpdated={loadData}
                            canStopCharging={canStopCharging} canAssignPromo={canAssignPromo} canReturnOps={canReturnOps}
                            discRows={discRows} kioskOffset={kioskOffset} hasRental={hasRental} />
                    )}
                </div>
                <div className={activeTab === "comms" ? undefined : styles.tabHidden}>
                    {mountedTabs.current.has("comms") && (
                        <CommsTab args={args} canReadEmails={canReadEmails} canReadNotes={canReadNotes} />
                    )}
                </div>
            </div>
        </GridEditModal>
        {showEditCart && (
            <EditCartModal
                open={showEditCart}
                instantID={args.instantID}
                onClose={() => setShowEditCart(false)}
                onSaved={loadData}
            />
        )}
        {eventPopupPath && (
            <PagePopup path={eventPopupPath} onClose={() => setEventPopupPath(null)} />
        )}
    </>
    );
}
